import { useCallback, useEffect, useState } from "react";
import { fetchAllGoogleLoginEvents, fetchMyGoogleLoginEvents } from "../../services/authApi";
import { formatDisplayDate } from "../../services/apiClient";
import { useAuth } from "../../hooks/useAuth";
import { getLocalLoginHistory, getLocalLoginHistoryForUser } from "../../util/loginHistory";
import "./GoogleLoginHistoryPanel.scss";

const mergeLoginEvents = (remoteItems = [], localItems = []) => {
  const map = new Map();

  [...remoteItems, ...localItems].forEach((entry) => {
    const key = entry.loginId || `${entry.email}-${entry.loggedInAt}`;
    if (!map.has(key)) {
      map.set(key, entry);
    }
  });

  return Array.from(map.values()).sort(
    (left, right) => new Date(right.loggedInAt).getTime() - new Date(left.loggedInAt).getTime(),
  );
};

const GoogleLoginHistoryPanel = ({ mode = "user", userEmail = "", limit = 8 }) => {
  const { logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const loadEvents = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    const localItems =
      mode === "admin" ? getLocalLoginHistory() : getLocalLoginHistoryForUser(userEmail);

    try {
      const remoteItems =
        mode === "admin" ? await fetchAllGoogleLoginEvents() : await fetchMyGoogleLoginEvents();
      setEvents(mergeLoginEvents(remoteItems, localItems).slice(0, limit));
    } catch (error) {
      setEvents(localItems.slice(0, limit));
      setErrorMessage(
        mode === "admin"
          ? "Server login history unavailable. Showing browser-only entries until GET /auth/google/login-events is live."
          : "Showing sign-in history saved in this browser until GET /auth/google/login-events/mine is live.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [limit, mode, userEmail]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    const handleHistoryChange = () => loadEvents();
    window.addEventListener("login-history-changed", handleHistoryChange);

    return () => {
      window.removeEventListener("login-history-changed", handleHistoryChange);
    };
  }, [loadEvents]);

  return (
    <section className="google-login-history">
      <div className="google-login-history__header">
        <h4 className="google-login-history__title">
          {mode === "admin" ? "Google sign-in history" : "History"}
        </h4>
        <div className="google-login-history__actions">
          {mode === "user" && (
            <button type="button" className="google-login-history__sign-out" onClick={logout}>
              Sign out
            </button>
          )}
          <button type="button" className="google-login-history__refresh" onClick={loadEvents}>
            Refresh
          </button>
        </div>
      </div>

      {isLoading && <p className="google-login-history__message">Loading sign-in history...</p>}
      {errorMessage && <p className="google-login-history__hint">{errorMessage}</p>}

      {!isLoading && !events.length && (
        <p className="google-login-history__message">No Google sign-ins recorded yet.</p>
      )}

      <div className="google-login-history__list">
        {events.map((entry) => (
          <article key={entry.loginId || `${entry.email}-${entry.loggedInAt}`} className="google-login-history__item">
            <div className="google-login-history__user">
              {entry.pictureUrl && (
                <img src={entry.pictureUrl} alt={entry.name || "User"} className="google-login-history__avatar" />
              )}
              <div>
                <p className="google-login-history__name">{entry.name || "Unknown user"}</p>
                <p className="google-login-history__email">{entry.email || "No email"}</p>
              </div>
            </div>
            <p className="google-login-history__meta">
              {formatDisplayDate(entry.loggedInAt)} · {entry.page || "/captionPublishing"}
            </p>
            {mode === "admin" && entry.userAgent && (
              <p className="google-login-history__agent">{entry.userAgent}</p>
            )}
          </article>
        ))}
      </div>
    </section>
  );
};

export default GoogleLoginHistoryPanel;
